import React, { useState, useEffect } from 'react';
import { X, Megaphone, Sparkles, AlertTriangle, Bell, Lightbulb, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const TYPE_CONFIG = {
  update: { icon: Sparkles, color: 'blue', label: 'System Update' },
  admin_message: { icon: Megaphone, color: 'emerald', label: 'Message' },
  notice: { icon: AlertTriangle, color: 'amber', label: 'Notice' },
};

export function AnnouncementsAdmin({ isOpen, onClose, toast }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [featureTitle, setFeatureTitle] = useState('');
  const [featureDesc, setFeatureDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Prefetch announcements as soon as the component mounts, so the modal
    // opens INSTANTLY with content (no empty "Loading..." shell flash).
    loadAnnouncements();
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Refresh in background each time it's reopened (cheap; cached data
      // is already showing from the mount-time fetch).
      loadAnnouncements();
      setShowFeatureForm(false);
      setSubmitted(false);
    }
  }, [isOpen]);

  const loadAnnouncements = async () => {
    // Only show "Loading..." spinner if we don't have any cached data yet.
    setLoading((prev) => (announcements.length === 0 ? true : prev));
    try {
      const token = localStorage.getItem('dentalToken');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/admin/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) setAnnouncements(res.data.data);
    } catch {}
    setLoading(false);
  };

  const submitFeatureRequest = async () => {
    if (!featureTitle.trim()) {
      toast?.({ title: "Missing Title", description: "Please enter a title for your feature request.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('dentalToken');
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/feature-request`,
        { title: featureTitle.trim(), description: featureDesc.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setSubmitted(true);
        setFeatureTitle('');
        setFeatureDesc('');
        toast?.({ title: "Request Submitted", description: "Thank you! We'll review your feature request." });
      }
    } catch {
      toast?.({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="announcements-viewer-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-white" />
            <h2 className="text-white font-bold text-lg">Updates & News</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10" data-testid="close-announcements-viewer">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Announcements List */}
          {loading ? (
            <div className="text-center py-6 text-gray-500">Loading...</div>
          ) : announcements.filter(a => a.isActive).length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Bell className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No updates yet</p>
              <p className="text-sm mt-1">Check back after the next PractCom update.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.filter(a => a.isActive).map(ann => {
                const config = TYPE_CONFIG[ann.type] || TYPE_CONFIG.update;
                const Icon = config.icon;
                return (
                  <div key={ann.id} className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg bg-${config.color}-100 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon className={`h-4.5 w-4.5 text-${config.color}-600`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm">{ann.title}</h3>
                          {ann.version && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">v{ann.version}</span>
                          )}
                        </div>
                        {ann.senderName && ann.type === 'admin_message' && (
                          <p className="text-xs text-gray-500 mb-2">From: {ann.senderName}</p>
                        )}
                        <ul className="space-y-1.5">
                          {ann.items?.map((item, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-blue-400" />
                              {item.text}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-gray-400 mt-2">{new Date(ann.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Request a Feature Section */}
          <div className="mt-6 pt-5 border-t border-gray-200">
            {!showFeatureForm && !submitted ? (
              <button
                onClick={() => setShowFeatureForm(true)}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-dashed border-purple-200 rounded-xl hover:border-purple-400 hover:from-purple-100 hover:to-indigo-100 transition-all group"
                data-testid="request-feature-btn"
              >
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <Lightbulb className="h-5 w-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-purple-900">Request a New Feature</p>
                  <p className="text-sm text-purple-600">Have an idea? Let us know what would make PractCom better for you.</p>
                </div>
              </button>
            ) : submitted ? (
              <div className="text-center py-6" data-testid="feature-request-success">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="font-semibold text-gray-800">Thank You!</p>
                <p className="text-sm text-gray-500 mt-1">Your feature request has been submitted. Dr. Ganz will review it personally.</p>
                <button onClick={() => { setSubmitted(false); setShowFeatureForm(false); }} className="text-sm text-blue-600 hover:text-blue-700 mt-3 font-medium">
                  Submit Another Request
                </button>
              </div>
            ) : (
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-100" data-testid="feature-request-form">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">Request a New Feature</h3>
                </div>
                <div className="mb-3">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">What feature would you like?</label>
                  <input
                    value={featureTitle}
                    onChange={e => setFeatureTitle(e.target.value)}
                    placeholder="e.g., Appointment reminders via text"
                    className="w-full px-3 py-2.5 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white"
                    data-testid="feature-title-input"
                  />
                </div>
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Tell us more (optional)</label>
                  <textarea
                    value={featureDesc}
                    onChange={e => setFeatureDesc(e.target.value)}
                    placeholder="Describe how this feature would help your practice..."
                    rows={3}
                    className="w-full px-3 py-2.5 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white resize-none"
                    data-testid="feature-desc-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={submitFeatureRequest}
                    disabled={submitting}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="submit-feature-btn"
                  >
                    {submitting ? (
                      <><div className="h-4 w-4 border-2 border-white/50 border-t-white rounded-full animate-spin mr-2" />Sending...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" />Submit Request</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setShowFeatureForm(false)} className="text-gray-600">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
